import React from 'react';
import {
  Activity,
  CheckCircle2,
  CircleDashed,
  FileText,
  Lock,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  AgentActionPreview,
  AgentArtifact,
  AgentCapabilityManifest,
  AgentExecutionDecision,
  AgentProviderGuardrail,
  AgentRunResponse,
  AgentRuntimePolicy,
  AgentRuntimeStep,
  AgentRunTimelineEvent,
} from '@/data/agentControlRoom';

const decisionTone: Record<string, string> = {
  allowed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  draft_only: 'border-blue-200 bg-blue-50 text-blue-700',
  blocked: 'border-rose-200 bg-rose-50 text-rose-700',
  requires_policy: 'border-amber-200 bg-amber-50 text-amber-700',
  requires_stronger_mode: 'border-amber-200 bg-amber-50 text-amber-700',
};

const previewTone: Record<string, string> = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  submitted: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  draft_only: 'border-blue-200 bg-blue-50 text-blue-700',
  blocked: 'border-rose-200 bg-rose-50 text-rose-700',
  cancelled: 'border-slate-200 bg-slate-100 text-slate-600',
};

const timelineIcon: Record<string, React.ReactNode> = {
  observe: <Activity className="h-3.5 w-3.5" aria-hidden="true" />,
  reason: <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />,
  tool_call: <CircleDashed className="h-3.5 w-3.5" aria-hidden="true" />,
  draft: <FileText className="h-3.5 w-3.5" aria-hidden="true" />,
  simulate: <Activity className="h-3.5 w-3.5" aria-hidden="true" />,
  risk_check: <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />,
  action_preview: <Lock className="h-3.5 w-3.5" aria-hidden="true" />,
  action_submitted: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />,
  blocked: <XCircle className="h-3.5 w-3.5" aria-hidden="true" />,
  succeeded: <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />,
  failed: <XCircle className="h-3.5 w-3.5" aria-hidden="true" />,
  cancelled: <XCircle className="h-3.5 w-3.5" aria-hidden="true" />,
};

function compactLabel(value: string) {
  return value.replace(/_/g, ' ');
}

function formatTime(value?: string | null) {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(parsed));
}

function formatValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (value == null) return '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function ChipList({
  items,
  emptyLabel,
  tone = 'slate',
}: {
  items?: string[];
  emptyLabel: string;
  tone?: 'slate' | 'emerald' | 'rose';
}) {
  const values = (items ?? []).filter(Boolean);
  if (values.length === 0) {
    return <span className="text-xs text-slate-400">{emptyLabel}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((item) => (
        <span
          key={item}
          className={cn(
            'rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]',
            tone === 'emerald' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
            tone === 'rose' && 'border-rose-200 bg-rose-50 text-rose-700',
            tone === 'slate' && 'border-slate-200 bg-slate-50 text-slate-600',
          )}
        >
          {compactLabel(item)}
        </span>
      ))}
    </div>
  );
}

export function AgentExecutionDecisionBadge({
  decision,
}: {
  decision?: AgentExecutionDecision | null;
}) {
  if (!decision) {
    return (
      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">
        No decision
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={cn('font-medium capitalize', decisionTone[decision.status] ?? decisionTone.blocked)}>
      {compactLabel(decision.status)}
    </Badge>
  );
}

export function AgentCapabilityScopePanel({
  manifest,
  className,
}: {
  manifest?: AgentCapabilityManifest | null;
  className?: string;
}) {
  if (!manifest) return null;
  return (
    <section
      className={cn('rounded-xl border border-slate-200 bg-white p-3 shadow-sm', className)}
      data-testid="agent-capability-scope"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Capability scope</p>
          <p className="mt-1 text-sm font-semibold text-slate-950">
            {manifest.route} · {manifest.executionMode}
          </p>
        </div>
        <ShieldCheck className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Allowed</p>
          <ChipList items={manifest.allowedCapabilities} emptyLabel="No capabilities granted" tone="emerald" />
        </div>
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Blocked</p>
          <ChipList items={manifest.blockedCapabilities} emptyLabel="No blocked capabilities" tone="rose" />
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Runtime tools</p>
        <ChipList items={manifest.allowedTools} emptyLabel="No runtime tools selected" />
      </div>
      {manifest.policyNotes.length > 0 ? (
        <ul className="mt-3 space-y-1 text-xs leading-5 text-slate-500">
          {manifest.policyNotes.slice(0, 3).map((note) => (
            <li key={note} className="flex gap-2">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function AgentActionPreviewCard({
  preview,
  onSubmit,
  onCancel,
  isBusy,
}: {
  preview: AgentActionPreview;
  onSubmit?: (preview: AgentActionPreview) => void;
  onCancel?: (preview: AgentActionPreview) => void;
  isBusy?: boolean;
}) {
  const canSubmit = preview.canSubmit && preview.status === 'ready';
  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      data-testid="agent-action-preview"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Action preview</p>
            <Badge variant="outline" className={cn('capitalize', previewTone[preview.status] ?? previewTone.blocked)}>
              {compactLabel(preview.status)}
            </Badge>
          </div>
          <h3 className="mt-1 text-sm font-semibold text-slate-950">{preview.title}</h3>
          <p className="mt-1 text-sm leading-5 text-slate-600">{preview.summary}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-lg"
            disabled={isBusy || preview.status === 'cancelled'}
            onClick={() => onCancel?.(preview)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8 rounded-lg"
            disabled={!canSubmit || isBusy}
            onClick={() => onSubmit?.(preview)}
          >
            {preview.status === 'draft_only' ? 'Draft only' : 'Submit preview for review'}
          </Button>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Expected effects</p>
          <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
            {preview.expectedEffects.map((effect) => (
              <li key={effect} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden="true" />
                <span>{effect}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Policy checks</p>
          <ChipList items={preview.requiredPolicyChecks} emptyLabel="No checks listed" />
          <p className="mt-2 text-xs leading-5 text-slate-500">{preview.riskPosture}</p>
          <div className="mt-2 grid gap-1.5 text-[11px] text-slate-500">
            <span>
              Required capability:{' '}
              <strong className="font-semibold text-slate-700">{compactLabel(preview.requiredCapability)}</strong>
            </span>
            <span>
              Idempotency:{' '}
              <strong className="font-semibold text-slate-700">
                {preview.idempotencyRequired ? 'required before any mutation' : 'not required'}
              </strong>
            </span>
            {!canSubmit ? (
              <span className="font-medium text-rose-600">
                Submission is disabled because this preview is {compactLabel(preview.status)}.
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export function AgentArtifactCard({ artifact }: { artifact: AgentArtifact }) {
  const payloadEntries = Object.entries(artifact.redactedPayload ?? {}).slice(0, 4);
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-artifact">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600">
          <FileText className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
            {compactLabel(artifact.kind)}
          </p>
          <h3 className="mt-1 text-sm font-semibold text-slate-950">{artifact.title}</h3>
          <p className="mt-1 text-sm leading-5 text-slate-600">{artifact.summary}</p>
        </div>
      </div>
      {payloadEntries.length > 0 ? (
        <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          {payloadEntries.map(([key, value]) => (
            <div key={key} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">{compactLabel(key)}</dt>
              <dd className="mt-1 truncate text-slate-600">{String(value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div className="mt-3">
        <ChipList items={artifact.evidenceRefs} emptyLabel="No evidence references" />
      </div>
    </section>
  );
}

export function AgentRunTimeline({
  events,
  compact = false,
}: {
  events?: AgentRunTimelineEvent[];
  compact?: boolean;
}) {
  const sortedEvents = [...(events ?? [])].sort((a, b) => a.seq - b.seq);
  if (sortedEvents.length === 0) {
    return null;
  }
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-run-timeline">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Run timeline</p>
          <p className="mt-1 text-sm font-semibold text-slate-950">{sortedEvents.length} recorded events</p>
        </div>
        <Activity className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </div>
      <ol className={cn('mt-3 space-y-2', compact && 'max-h-64 overflow-y-auto pr-1')}>
        {sortedEvents.map((event) => (
          <li key={event.id} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-2">
            <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
              {timelineIcon[event.kind] ?? <CircleDashed className="h-3.5 w-3.5" aria-hidden="true" />}
            </div>
            <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">{event.title}</p>
                <span className="text-[11px] text-slate-400">{formatTime(event.createdAt)}</span>
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-600">{event.detail}</p>
              {event.evidenceRef ? (
                <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.1em] text-slate-400">
                  Evidence {event.evidenceRef}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AgentRunEvidencePanel({ run }: { run: AgentRunResponse }) {
  const evidenceRefs = run.evidenceRefs ?? [];
  const suggestedActions = run.suggestedActions ?? [];
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-run-evidence">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600">
          <FileText className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Run evidence</p>
          <p className="mt-1 text-sm leading-5 text-slate-700">{run.answer}</p>
        </div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Evidence refs</p>
          <ChipList items={evidenceRefs} emptyLabel="No evidence refs" />
        </div>
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Suggested actions</p>
          <ChipList items={suggestedActions} emptyLabel="No suggested actions" />
        </div>
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Prompt hash</dt>
          <dd className="mt-1 truncate font-mono text-slate-600">{run.promptHash}</dd>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Response hash</dt>
          <dd className="mt-1 truncate font-mono text-slate-600">{run.responseHash ?? 'pending'}</dd>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Trace</dt>
          <dd className="mt-1 truncate font-mono text-slate-600">{run.traceId ?? 'not attached'}</dd>
        </div>
        <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Provider</dt>
          <dd className="mt-1 truncate text-slate-600">{run.provider} · {run.model}</dd>
        </div>
      </dl>
      {run.errorCode || run.errorMessage ? (
        <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <strong>{run.errorCode ?? 'runtime_error'}:</strong> {run.errorMessage ?? 'Run failed closed.'}
        </div>
      ) : null}
      {run.contractWarnings?.length ? (
        <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
          <strong>Runtime contract warning:</strong> {run.contractWarnings[0]}
        </div>
      ) : null}
    </section>
  );
}

function AgentGuardrailsPanel({ guardrails }: { guardrails?: AgentProviderGuardrail[] }) {
  const items = guardrails ?? [];
  if (items.length === 0) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-run-guardrails">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Guardrails</p>
      <div className="mt-3 grid gap-2">
        {items.map((item) => (
          <div key={item.code} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">{compactLabel(item.code)}</p>
              <Badge variant="outline" className={item.passed ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}>
                {item.passed ? 'Passed' : 'Failed'}
              </Badge>
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-600">{item.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AgentRuntimePolicyPanel({ policy }: { policy?: AgentRuntimePolicy | null }) {
  if (!policy) return null;
  const values = [
    ['Provider', policy.provider],
    ['Real providers', policy.realProvidersEnabled ? 'enabled' : 'disabled'],
    ['External tools', policy.allowExternalTools ? 'enabled' : 'disabled'],
    ['Prompt retention', policy.localPromptRetentionPolicy],
    ['Provider retention', policy.providerRetentionPolicy],
    [
      'Approval authority',
      policy.requireHumanApproval
        ? 'external gates required'
        : 'runtime cannot approve or execute actions',
    ],
  ];
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-runtime-policy">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Runtime policy</p>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
        {values.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</dt>
            <dd className="mt-1 text-slate-700">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3">
        <ChipList items={policy.allowedTools} emptyLabel="No tools allowed" />
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">
        Risk, OMS, policy, and approval gates remain authoritative outside Agent Runtime.
      </p>
    </section>
  );
}

function AgentRunStepsPanel({ steps }: { steps?: AgentRuntimeStep[] }) {
  const items = steps ?? [];
  if (items.length === 0) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="agent-run-steps">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Runtime steps</p>
      <div className="mt-3 space-y-2">
        {items.map((step) => (
          <div key={step.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">
                {step.seq}. {step.title}
              </p>
              <Badge variant="outline" className="capitalize">{compactLabel(step.status)}</Badge>
            </div>
            <dl className="mt-2 grid gap-2 text-[11px] sm:grid-cols-3">
              <div>
                <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Input hash</dt>
                <dd className="truncate font-mono text-slate-600">{step.inputHash ?? 'none'}</dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Output hash</dt>
                <dd className="truncate font-mono text-slate-600">{step.outputHash ?? 'none'}</dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-[0.1em] text-slate-400">Span</dt>
                <dd className="truncate font-mono text-slate-600">{step.spanId ?? 'none'}</dd>
              </div>
            </dl>
            {step.toolCalls.length > 0 ? (
              <div className="mt-2 space-y-1.5">
                {step.toolCalls.map((tool) => (
                  <div key={tool.id} className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">{tool.toolName}</span>
                      <span>{tool.status} · {tool.durationMs}ms</span>
                    </div>
                    <p className="mt-1 truncate font-mono text-slate-500">
                      args {tool.argumentsHash ?? 'none'} · result {tool.resultHash ?? 'none'}
                    </p>
                    {tool.errorCode ? <p className="mt-1 text-rose-600">{tool.errorCode}</p> : null}
                  </div>
                ))}
              </div>
            ) : null}
            {(Object.keys(step.redactedInput).length > 0 || Object.keys(step.redactedOutput).length > 0) ? (
              <details className="mt-2 text-[11px] text-slate-500">
                <summary className="cursor-pointer font-semibold text-slate-600">Redacted payload preview</summary>
                <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-slate-950 p-2 text-[10px] text-slate-100">
                  {formatValue({ input: step.redactedInput, output: step.redactedOutput })}
                </pre>
              </details>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

export function AgenticRunInspector({
  run,
  title = 'Agent Runtime run',
  className,
  compactTimeline = false,
  busyPreviewId,
  onSubmitPreview,
  onCancelPreview,
}: {
  run?: AgentRunResponse | null;
  title?: string;
  className?: string;
  compactTimeline?: boolean;
  busyPreviewId?: string | null;
  onSubmitPreview?: (preview: AgentActionPreview) => void;
  onCancelPreview?: (preview: AgentActionPreview) => void;
}) {
  if (!run) return null;
  return (
    <section className={cn('space-y-3', className)} data-testid="agent-run-inspector">
      <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-950">{run.id}</p>
            <Badge variant="outline" className="capitalize">
              {compactLabel(run.status)}
            </Badge>
            <AgentExecutionDecisionBadge decision={run.executionDecision} />
          </div>
        </div>
        <p className="text-xs leading-5 text-slate-500">{run.executionImpact}</p>
      </div>
      <AgentRunEvidencePanel run={run} />
      <AgentCapabilityScopePanel manifest={run.capabilityManifest} />
      <AgentGuardrailsPanel guardrails={run.guardrails} />
      {run.actionPreviews?.map((preview) => (
        <AgentActionPreviewCard
          key={preview.id}
          preview={preview}
          isBusy={busyPreviewId === preview.id}
          onSubmit={onSubmitPreview}
          onCancel={onCancelPreview}
        />
      ))}
      {run.artifacts?.map((artifact) => (
        <AgentArtifactCard key={artifact.id} artifact={artifact} />
      ))}
      <AgentRuntimePolicyPanel policy={run.runtimePolicy} />
      <AgentRunStepsPanel steps={run.steps} />
      <AgentRunTimeline events={run.timeline} compact={compactTimeline} />
    </section>
  );
}
